import { APPLE_QUERY_PARAM, OTP_PROOFKEY } from '@/lib/consts/auth';
import { LOGIN_QUERY_PARAMS, URL_PARAMS } from '@/lib/consts/params';

const JOURNEY_ID_PATTERN = /^[a-zA-Z0-9-]{1,128}$/;

/**
 * What kind of handoff a landing arrived on.
 *
 * Publishers measure the widget -> web step by looking for a login or a page
 * event per player, and count anything else as a drop-off. A player who opens
 * the store from the game and leaves before their credential is exchanged
 * produces neither, so the visit is indistinguishable from never having
 * happened — which is exactly the report we keep receiving.
 *
 * Deliberately the credential *type* and never a value: these are live login
 * credentials, and the store has been putting them in RUM page URLs for months.
 * Nothing here writes one anywhere.
 */
export enum EStoreLandingCredential {
    /** From the game, the case publishers ask about. Same shape as the portal's. */
    OTP = 'otp',
    /** Handed over by the portal, so a store landing can be joined to a portal one. */
    PORTAL_TOKEN = 'portal-token',
    JWT = 'jwt',
    APPLE = 'apple',
    NONE = 'none',
}

/**
 * Which parameter carries the credential for each kind of landing, and so which
 * value is hashed into the join key.
 *
 * Apple has no parameter to hash: the credential arrives in a URL fragment the
 * server never sees, so those landings are counted without a join key.
 */
const CREDENTIAL_PARAMS: Partial<Record<EStoreLandingCredential, string>> = {
    [EStoreLandingCredential.OTP]: OTP_PROOFKEY,
    [EStoreLandingCredential.PORTAL_TOKEN]: LOGIN_QUERY_PARAMS.REDIRECT_LOGIN_TOKEN,
    [EStoreLandingCredential.JWT]: URL_PARAMS.JWT_PARAM,
};

/**
 * Credential types only, resolved from the query the request arrived with.
 *
 * Ordered by how much a landing tells us: the OTP handoff is the journey being
 * asked about, so it wins over a token that may be present alongside it.
 * Deliberately mirrors isLoginAuthCallback, which decides the same thing for
 * routing — anything it treats as a login callback should be counted as a
 * landing, or the funnel would be measuring a different population than the one
 * the router serves.
 */
export const resolveStoreLandingCredential = (params: URLSearchParams): EStoreLandingCredential => {
    if (params.get(OTP_PROOFKEY)) {
        return EStoreLandingCredential.OTP;
    }
    if (params.get(LOGIN_QUERY_PARAMS.REDIRECT_LOGIN_TOKEN)) {
        return EStoreLandingCredential.PORTAL_TOKEN;
    }
    if (params.get(URL_PARAMS.JWT_PARAM)) {
        return EStoreLandingCredential.JWT;
    }
    if (params.get(APPLE_QUERY_PARAM) === 'true') {
        return EStoreLandingCredential.APPLE;
    }

    return EStoreLandingCredential.NONE;
};

/** The value a landing is joined on, before it is hashed. Never logged as it is. */
export const readStoreLandingCredentialValue = (
    params: URLSearchParams,
    credential: EStoreLandingCredential
): string | null => {
    const param = CREDENTIAL_PARAMS[credential];

    return param ? params.get(param) : null;
};

export const readStoreJourneyId = (
    params: URLSearchParams
): string | undefined => {
    const journeyId = params.get(LOGIN_QUERY_PARAMS.JOURNEY_ID);
    if (!journeyId || !JOURNEY_ID_PATTERN.test(journeyId)) {
        return undefined;
    }
    return journeyId;
};
